import { FC, useState, useEffect, useRef } from 'react';
import { SourceTypeEnums } from '../../Molecules';
import { keyCheck, keyCheckForNav, KeyConstants, useOrientationHook } from '../../../utils';
import type { ProductGalleryV2Props, IGalleryState } from './types/propsTypes';
import { ProductGalleryStyleV2Type, SwipeEnum, SelectorIdEnum, MediaType, ScrollBehaviorEnum } from './types/enums';
import {
  getGalleryArray,
  getScrollPosition,
  getClassName,
  getScrollBehavior,
  getHeroMediaList,
} from './productGalleryV2Util';
import { renderNavigationBtn, thumbnailTray, heroGallery } from './ProductGalleryV2CTAs';
import styles from './ProductGalleryV2.module.scss';

let scrollTimer: NodeJS.Timeout | undefined;
let changeGalleryTimer: NodeJS.Timeout | undefined;

const ProductGalleryV2: FC<ProductGalleryV2Props> = ({
  galleryList,
  openModal,
  galleryId = 0,
  sourceKey,
  productGalleryStyleType,
}: ProductGalleryV2Props): JSX.Element => {
  const mediaList = getGalleryArray(galleryList || []);
  const mediaListLength = mediaList.length;
  const isGalleryEmpty = mediaListLength === 0;
  const shouldShowChevrons = mediaListLength > 3;
  const isModal = sourceKey === SourceTypeEnums.MODAL_GALLERY;

  const heroGalleryRef = useRef<HTMLDivElement>(null);
  const thumbnailRef = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState<number>(Number(galleryId));
  const [isPortrait, setIsPortrait] = useState<boolean>(true);
  const [shouldSmoothScroll, setShouldSmoothScroll] = useState<boolean>(false);
  const [scrollDirection, setScrollDirection] = useState<SwipeEnum | undefined>(SwipeEnum.NONE);
  const [prevScrollDirection, setPrevScrollDirection] = useState<SwipeEnum | undefined>(SwipeEnum.NONE);
  const [isScrollUserTriggered, setIsScrollUserTriggered] = useState<boolean>(false);
  const [showDummy, setShowDummy] = useState<boolean>(false);
  const [customClassName, setCustomClassName] = useState<string>('');
  const [updatedMediaList, setUpdatedMediaList] = useState<IGalleryState[]>(
    getHeroMediaList(mediaList, Number(galleryId)),
  );

  useOrientationHook((e, isChangeEventAdded = false) => {
    if (isChangeEventAdded) {
      setIsPortrait(!!(e && e.matches));
    }
  });

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const ref = heroGalleryRef?.current;
    if (ref && isScrollUserTriggered) {
      const scrollLeft = (e?.target as HTMLDivElement)?.scrollLeft;
      const isScrolledToLast = Math.abs(Math.round(ref?.scrollWidth - ref?.scrollLeft) - ref?.clientWidth) <= 4;
      let direction: SwipeEnum;
      if (scrollLeft === 0) {
        direction = SwipeEnum.LEFT;
      } else if (isScrolledToLast) {
        direction = SwipeEnum.RIGHT;
      } else {
        direction = SwipeEnum.NONE;
      }
      setScrollDirection((prevState: SwipeEnum | undefined) => {
        if (prevState !== direction) {
          setPrevScrollDirection(prevState);
          return direction;
        }
        return SwipeEnum.NONE;
      });
    }
  };

  useEffect(() => {
    if (
      scrollDirection !== prevScrollDirection &&
      [SwipeEnum.LEFT, SwipeEnum.RIGHT].includes(scrollDirection as SwipeEnum)
    ) {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        setShowDummy(true);
        swipeImage(scrollDirection as SwipeEnum);
      }, 100);
    }
  }, [scrollDirection]);

  useEffect(() => {
    changeGalleryImage();
  }, [galleryId]);

  useEffect(() => {
    setTimeout(() => setUpdatedMediaList(getHeroMediaList(mediaList, activeIndex)), customClassName ? 450 : 0);
  }, [activeIndex]);

  useEffect(() => {
    clearTimeout(changeGalleryTimer);
    changeGalleryTimer = setTimeout(() => changeGalleryImage(), 100);
  }, [updatedMediaList]);

  const changeGalleryImage = () => {
    const thumbnailTrayDom = thumbnailRef?.current;
    const slidesDom = heroGalleryRef?.current;
    thumbnailTrayDom?.scroll({
      left: getScrollPosition(thumbnailTrayDom) * (activeIndex - 1),
      top: 0,
      behavior: getScrollBehavior(shouldSmoothScroll),
    });
    slidesDom?.scroll({ left: getScrollPosition(slidesDom), top: 0, behavior: ScrollBehaviorEnum.AUTO });
    setIsScrollUserTriggered(true);
    (thumbnailTrayDom?.children[activeIndex] as HTMLButtonElement)?.focus({ preventScroll: true });
    setScrollDirection(SwipeEnum.NONE);
    setPrevScrollDirection(SwipeEnum.NONE);
    setTimeout(
      () => {
        setShowDummy(false);
        setCustomClassName('');
      },
      customClassName ? 0 : 300,
    );
  };

  const swipeImage = (swipe: SwipeEnum) => {
    if (showDummy) {
      return;
    }
    if (mediaListLength > 0) {
      let activeId = activeIndex;
      let isSmooth = true;
      const lastIndex = mediaListLength - 1;

      if (swipe === SwipeEnum.LEFT) {
        activeId = activeIndex === 0 ? lastIndex : activeIndex - 1;
        isSmooth = activeIndex > 0;
      } else {
        activeId = activeIndex === lastIndex ? 0 : activeIndex + 1;
        isSmooth = activeIndex < lastIndex;
      }
      setShouldSmoothScroll(isSmooth);
      setActiveIndex(activeId);
    }
  };

  const checkOpenGallery = (e: React.KeyboardEvent) => {
    if (keyCheck(e.code)) {
      openGalleryModal();
    }
  };

  const openGalleryModal = () => {
    if (!isGalleryEmpty && !showDummy) {
      openModal?.(activeIndex);
    }
  };

  const handleNavClick = (direction: SwipeEnum) => {
    if (showDummy) {
      return;
    }
    setIsScrollUserTriggered(true);
    let leftPos = 0;
    if (direction === SwipeEnum.RIGHT) {
      leftPos = heroGalleryRef?.current?.scrollWidth || 1000;
    }
    heroGalleryRef?.current?.scroll({ left: leftPos, top: 0, behavior: ScrollBehaviorEnum.SMOOTH });
  };

  const handleKeyBoardNavigation = (e: React.KeyboardEvent, direction?: SwipeEnum) => {
    let dir: SwipeEnum | undefined;
    if (keyCheck(e.code)) {
      dir = direction;
    } else if (keyCheckForNav(e.code)) {
      dir = KeyConstants.LEFT_NAV === e.code ? SwipeEnum.LEFT : SwipeEnum.RIGHT;
    }
    if (dir) {
      handleNavClick(dir);
    }
  };

  const onThumbnailClick = (index: number) => {
    if (showDummy || index === activeIndex || mediaListLength <= 1) {
      return;
    }
    setShouldSmoothScroll(true);
    setCustomClassName(styles[`${index < activeIndex ? 'left-right' : 'right-left'}-transition`]);
    setShowDummy(true);
    setIsScrollUserTriggered(false);
    setActiveIndex(index);
  };

  const navBtnParams = {
    direction: SwipeEnum.LEFT,
    shouldShowChevrons,
    handleNavClick,
    handleKeyBoardNavigation,
    styles,
  };

  const heroGalleryParams = {
    styles,
    sourceKey,
    isGalleryEmpty,
    isModal,
    showDummy,
  };

  const mainLabel = mediaList[activeIndex]?.main?.altText || '';

  return (
    <div
      className={`${styles[getClassName(productGalleryStyleType)]} ${styles['sticky']} ${
        mediaList?.[activeIndex]?.type === MediaType.VIDEO
          ? styles[`cpw-gallery-container-${isPortrait ? 'portrait' : 'landscape'}`]
          : ''
      }`}
    >
      <div
        id={`hero-gallery-${sourceKey}`}
        className={`${styles['hero-gallery']} ${isModal && styles['initial-pointer']} ${
          isGalleryEmpty && styles['galleryEmpty']
        }`}
        data-testid="hero-gallery-test-id"
        role="button"
        aria-label={mainLabel}
        onClick={openGalleryModal}
        onKeyUp={checkOpenGallery}
        tabIndex={0}
      >
        <div
          className={styles['hero-gallery-slides']}
          id={`${SelectorIdEnum.HERO_GALLERY_SLIDES}-${sourceKey}`}
          data-testid={`${SelectorIdEnum.HERO_GALLERY_SLIDES}-${sourceKey}`}
          ref={heroGalleryRef}
          onScroll={handleScroll}
        >
          {updatedMediaList?.map((media: IGalleryState, index: number) =>
            heroGallery({
              ...heroGalleryParams,
              media,
              index,
            }),
          )}
          {mediaListLength > 1 &&
            heroGallery({
              ...heroGalleryParams,
              media: mediaList[activeIndex],
              index: -1,
              className: customClassName,
            })}
        </div>
      </div>
      <div className={styles['galleryContainerScroll']} data-testid="gallery-test-id">
        {renderNavigationBtn({
          ...navBtnParams,
        })}
        <div
          className={styles['galleryContainerInnerScroll']}
          id={`${SelectorIdEnum.GALLERY_CONTAINER_INNER_SCROLL}-${sourceKey}`}
          data-testid="gallery-container-inner-container"
          ref={thumbnailRef}
        >
          {!isGalleryEmpty &&
            thumbnailTray({ mediaList, activeIndex, onThumbnailClick, handleKeyBoardNavigation, sourceKey, styles })}
        </div>
        {renderNavigationBtn({
          ...navBtnParams,
          direction: SwipeEnum.RIGHT,
        })}
      </div>
    </div>
  );
};

export default ProductGalleryV2;
export { ProductGalleryStyleV2Type };
export type { ProductGalleryV2Props };
